import { useEffect, useRef, useState } from "react";
import { Form } from "react-router-dom";
import { MapPin, Search, SlidersHorizontal } from "lucide-react";
import { useDispatch } from "react-redux";

import SearchAutocomplete from "@/pages/components/search-autocomplete";
import { HcbbSearchAutocomplete } from "@/pages/components/hcbb-cost-estimator/hcbb-search-autocomplete";
import { HcbbAdvancedSearchFiltersPanel } from "@/pages/components/hcbb-cost-estimator/hcbb-advanced-search-filters";
import SingleSelect from "@/pages/components/single-slect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { HCBB_DEFAULT_DISTANCE, HCBB_DISTANCE_OPTIONS } from "@/constants";
import {
  setDistance,
  setLocation,
} from "@/store/price transparency/price-transparency-slice";
import { useLocationSearch } from "@/hooks/use-cost-estimator";
import {
  useHcbbAdvancedFilters,
  useHcbbCommittedSearch,
  useHcbbFreeTextSearch,
  useHcbbProcedureDetails,
  useHcbbSearch,
} from "@/hooks/use-hcbb-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import {
  isHcbbSearchComplete,
  toHcbbResultKey,
} from "@/lib/hcbb-cost-estimator-utils";
import { cn } from "@/lib/utils";
import type { OptionType } from "@/types";
import type Distance from "@/model/distance";
import type Place from "@/model/place";

type SearchParams = { queryString: string };

/**
 * Matches `reveal-height-out`'s own duration in tailwind.config.js — only the
 * closing side needs a JS timer (below), to keep the panel mounted long
 * enough to play it; opening plays `reveal-height` via CSS alone.
 */
const ADVANCED_SEARCH_PANEL_CLOSE_ANIMATION_MS = 300;

type HcbbSearchFormProps = {
  /**
   * Shows the advanced-search trigger. Off by default because this form is
   * shared with the search/landing page, which has no results yet to filter.
   */
  showAdvancedSearch?: boolean;
};

export const HcbbSearchForm = ({
  showAdvancedSearch = false,
}: HcbbSearchFormProps) => {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();

  const searchQuery = useHcbbFreeTextSearch();
  const locationSearchQuery = useLocationSearch<Place>(userInfo);

  const {
    procedure,
    targetLocation,
    distance,
    isSearchable,
    selectSuggestion,
    clearSelection,
    submitSearch,
  } = useHcbbSearch();

  // The *committed* search, not the in-progress values above: if the member
  // edits the form without pressing Search again, those would point at a
  // different result set than the one `HcbbSearchResults` is still showing.
  // Keying off the committed search guarantees this button always targets the
  // same `resultKey` that page is rendering, with no prop threading between
  // the two sibling components.
  const committedSearch = useHcbbCommittedSearch();
  const advancedSearchResultKey = toHcbbResultKey(committedSearch);
  const advancedFilters = useHcbbAdvancedFilters(advancedSearchResultKey);

  // Read a second time, purely for the panel embedded below — `useQuery`
  // dedupes by query key regardless of which component subscribes, so this
  // is a free cache read (not a second network call) once
  // `HcbbSearchResults`, a sibling, has resolved the same committed search.
  // `enabled` inside the hook is already `false` until both a procedure and
  // a location are picked, so this is a no-op on the plain search page,
  // where `showAdvancedSearch` (and so the toggle/panel below) never render.
  const { data: procedureDetails } = useHcbbProcedureDetails(committedSearch);
  const facilities = procedureDetails?.data?.facilities ?? [];
  const professionals = procedureDetails?.data?.professionals ?? [];
  const hasProviders = facilities.length > 0 || professionals.length > 0;
  const showAdvancedSearchPanel = advancedFilters.isPanelOpen && hasProviders;

  // Kept mounted for one extra animation's worth of time after
  // `showAdvancedSearchPanel` goes false, so `reveal-height-out` has
  // something to play on the way out instead of the panel vanishing
  // instantly — a plain conditional render would unmount it on the same
  // tick `isPanelOpen` flips.
  const [isPanelMounted, setIsPanelMounted] = useState(showAdvancedSearchPanel);
  const [isPanelClosing, setIsPanelClosing] = useState(false);

  useEffect(() => {
    if (showAdvancedSearchPanel) {
      setIsPanelMounted(true);
      setIsPanelClosing(false);
      return;
    }
    if (!isPanelMounted) return;

    setIsPanelClosing(true);
    const timeout = setTimeout(() => {
      setIsPanelMounted(false);
      setIsPanelClosing(false);
    }, ADVANCED_SEARCH_PANEL_CLOSE_ANIMATION_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `isPanelMounted` is read, not a trigger: re-running this on its own change would immediately re-close a panel that just finished opening.
  }, [showAdvancedSearchPanel]);

  const advancedSearchTriggerRef = useRef<HTMLButtonElement>(null);
  const wasAdvancedSearchOpenRef = useRef(advancedFilters.isPanelOpen);

  // The panel has no backdrop of its own to catch a click elsewhere, so
  // closing it is otherwise only reachable through its own header/footer
  // controls — unmounting it on close discards the draft for free.
  useEffect(() => {
    if (wasAdvancedSearchOpenRef.current && !advancedFilters.isPanelOpen) {
      advancedSearchTriggerRef.current?.focus();
    }
    wasAdvancedSearchOpenRef.current = advancedFilters.isPanelOpen;
  }, [advancedFilters.isPanelOpen]);

  return (
    <div className="rounded-lg border shadow-md">
      <Form method="post">
        <div className="flex gap-2 p-6">
          <div className="flex-1">
            <HcbbSearchAutocomplete
              label="SEARCH"
              placeholder="Procedure | Doctor | Facility | Specialty"
              placeholderIcon={Search}
              value={procedure?.description}
              queryFunction={searchQuery}
              onSelect={selectSuggestion}
              onClear={clearSelection}
              minSearchLength={3}
              debounceTime={300}
              resultLimit={6}
            />
          </div>

          <div className="flex-1">
            <SearchAutocomplete<Place, SearchParams>
              label="LOCATION"
              placeholder="Enter a location"
              placeholderIcon={MapPin}
              value={targetLocation?.description}
              queryFunction={locationSearchQuery}
              formatResult={(result: Place) => {
                return (
                  <div key={result.place_id}>
                    <div className="text-sm font-medium">
                      {result.description}
                    </div>
                  </div>
                );
              }}
              onSelect={(value: Place) => {
                console.log(value);
                dispatch(setLocation(value));
              }}
              onClear={() => {
                dispatch(setLocation(null));
              }}
              minSearchLength={2}
              debounceTime={200}
            />
          </div>

          <div className="flex flex-1 items-end gap-2">
            <div className="flex-1 space-y-2">
              <Label>DISTANCE</Label>
              <SingleSelect
                placeholder="Select Distance"
                data={HCBB_DISTANCE_OPTIONS}
                defaultOption={HCBB_DEFAULT_DISTANCE}
                selectedOption={distance}
                readValueFor="displayName"
                searchEnabled={false}
                onSelect={(item: OptionType) => {
                  dispatch(setDistance(item as Distance));
                }}
              />
            </div>
            {showAdvancedSearch && (
              // `overflow-hidden` + `animate-reveal-width` on the wrapper is
              // what makes the DISTANCE field's `flex-1` above smoothly give
              // up room for this button on mount, instead of already being
              // squeezed on first paint. The slide (`animate-slide-in`) stays
              // on the button itself so the two effects can run at their own
              // speeds.
              <div className="animate-reveal-width overflow-hidden">
                <Button
                  ref={advancedSearchTriggerRef}
                  type="button"
                  variant="outline"
                  aria-label="Open advanced search filters"
                  aria-expanded={advancedFilters.isPanelOpen}
                  disabled={!isHcbbSearchComplete(committedSearch)}
                  onClick={() =>
                    advancedFilters.setPanelOpen(!advancedFilters.isPanelOpen)
                  }
                  // Plays once, the moment this button is first mounted — i.e.
                  // landing on the results page — sliding and fading in over
                  // 2s to draw the eye to a control that was not here before.
                  // Translate only, never scale: it settles at its own resting
                  // size throughout, so it never reads as expanding outward.
                  // A single self-contained entrance, not an enter/exit pair:
                  // it ends at rest, so there is nothing to reverse when it
                  // "disappears". Unrelated to the panel it opens — nothing
                  // here reacts to `isPanelOpen`.
                  className={cn(
                    "animate-slide-in",
                    advancedFilters.isPanelOpen &&
                      "border-primary bg-primary-extraLight text-primary hover:bg-primary-extraLight",
                  )}
                >
                  <SlidersHorizontal aria-hidden="true" />
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="default"
              disabled={!isSearchable}
              onClick={submitSearch}
            >
              Search
            </Button>
          </div>
        </div>
      </Form>

      {/* Nested directly in this card — not a `RightSidebar` slide-over, and
          not a second, visually separate box either — so a plain `border-t`
          divider is the only thing that reads as a "seam". Outside `<Form>`:
          Apply/Cancel/Clear here are unrelated to the search submission
          above. Mounted only while open, so its draft state seeds once from
          the committed filters at mount and needs no re-seeding effect. */}
      {isPanelMounted && (
        <div
          className={cn(
            "border-t",
            isPanelClosing
              ? "animate-reveal-height-out"
              : "animate-reveal-height",
          )}
        >
          <HcbbAdvancedSearchFiltersPanel
            resultKey={advancedSearchResultKey}
            facilities={facilities}
            professionals={professionals}
          />
        </div>
      )}
    </div>
  );
};
